<template lang="pug">
portal(to="modals")
  Modal(:show="show" title="Connected accounts" class="gray-bg" @close="$emit('close', true)")
    template(v-slot:subheader)
      .searchArea
        FormInput(type="text", :placeholder="$t('common.search')", v-model="searchString", id="user-email", :outline="true").ml-a.mr-a
    .connected-accounts-settings.p-a-4
      .d-f.ai-c.p-b-2
        Button(@click="addNewConnectedAccount()", style="white-space: nowrap", :class="`${!addConnectedAccount ? 'very-blue' : 'very-blue-light'}`", :disabled="!canCreateSubAccount") {{ $t('settings.create_account') }}
        small(v-if="!canCreateSubAccount").m-l-2 Contact support if you need to create additional accounts
        small(v-else).m-l-2 {{ canCreateNumAccounts }}
      div
        card-minimal-title(class="m-t-2", :title="`${total_accounts} accounts`")
        CardMinimal
          .connected-accounts(v-if="total_accounts > 0")
            .p-a-4.no-connected-accounts(v-if="total_accounts === 0"): p {{ searchString ? $t('settings.no_connected_accounts_matches') : $t('settings.no_connected_accounts') }}
          div
            ExpandableListItem(v-for="account in accounts", :key="account.id", :selected="selectedAccount === account.id" v-if="account.id !== user.account_id").no-first
              .p-a-4.d-f.ai-c(@click="selectAccount(account.id)")
                p {{ account.name }}
                Tag(:text="$t('settings.assumed').toUpperCase()", v-if="assumedAccount === account.id").ml-a
              template(slot="dots", v-if="assumable")
                ul
                  li.blue(@click="assumedAccount === account.id ? unassumeAccount() : assumeAccount(account.id)")
                    i.mdi(:class="`mdi-${assumedAccount === account.id ? 'eye-off' : 'eye'}`")
                    | {{ assumedAccount === account.id ? $t('settings.unassume') : $t('settings.assume') }}
              template(slot="expand")
                .d-f.f-g.fd-c
                  div
                    label Created
                    FormInput(readonly="readonly", :id="`${account.id}-created`", :copy="true", :value="created(account.created_at)")
                  div
                    label URL
                    FormInput(readonly="readonly", :id="`${account.id}-playurl`", :copy="true", :value="`https://${account.fqdn}/play`")
      Pagination(:page="current_page", :maxPage="max_page", @page="paginate", class="m-t-4 m-b-2")
      portal(to="modals")
        Modal(:show="addConnectedAccount", title="Create account" :stuck="createState !== ''" @close="cancelCreateAccount()").auto-height
          .p-a-4
            p.m-b-1 Account name
            FormInput(type="text", :placeholder="$t('Example')" v-model="newAccount.name", id="account-email", :outline="true").m-b-2
            p.m-b-1 FQDN&nbsp;
              small (If not set we will use account name to create an FQDN)
            FormInput(type="text", :placeholder="$t('examplefqdn')" v-model="newAccount.fqdn", id="account-name", :outline="true")
            div.m-t-1: p(v-html="accountFqdn").info.fqdn
            .d-f.m-t-2
              Button.m-r-4(@click="accountNameValid ? createAccount() : null", style="white-space: nowrap", :disabled="!accountNameValid", :class="`${accountNameValid ? 'very-blue' : 'very-blue-light'} ${createState === 'created' ? 'success' : ''}`" :icon="createState === 'creating' ? 'loading mdi-spin' : null",)
                template(v-if="createState === ''") {{ $t('settings.create_new_account') }}
                template(v-else-if="createState === 'creating'") {{ $t('settings.creating_new_account') }}
                template(v-else-if="createState === 'created'") {{ $t('settings.new_account_created') }}
              Button.ml-a.outline(@click="cancelCreateAccount()") {{ $t('common.cancel') }}
</template>

<script>

import moment from 'moment';

import ExpandableListItem from '@/components/Lists/ExpandableListItem.vue';
import Tag from '@/components/tag.vue';
import Pagination from '@/components/Lists/Pagination.vue';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ExpandableListItem,
    Tag,
    Pagination,
  },
  computed: {
    /**
     * Return an inviteurl
     * @returns {String}
     */
    inviteUrl() {
      return this.account ? `https://app.streamify.io/signup?i=${btoa(`streamify[]${this.account.id}`)}` : null;
    },
    canCreateNumAccounts() {
      return `Can create additional ${this.accountSettings.subscription.sub_accounts.additional} ${this.accountSettings.subscription.sub_accounts.additional > 1 ? 'accounts' : 'account'}`;
    },
    /**
     * Return a boolean if this account can create subaccounts
     * @returns {Boolean}
     */
    canCreateSubAccount() {
      if (this.accountSettings?.subscription?.sub_accounts?.additional > 0) {
        return true;
      }

      return false;
    },
    /**
     * Return id of account if assumed
     * @returns {Number}
     */
    assumedAccount() {
      return parseInt(this.$store.getters['User/assumed'] || this.$store.getters['User/assumeParam'], 10);
    },
    accountNameValid() {
      if (this.newAccount.fqdn !== '' && this.newAccount.fqdn.trim().length <= 2) {
        return false;
      }

      return this.newAccount.name.trim().length > 2;
    },
    accountFqdn() {
      const { newAccount: { name, fqdn } } = this;

      if (name === '' && fqdn === '') {
        // eslint-disable-next-line
        return `${this.$t('login.fqdn_example_addr_subdomain', { subdomain: `${this.$t('common.fqdn_example').replace(/[áàäâãåÁÀÄÂÃÅ]/g, 'a').replace(/[óòöôõÓÒÖÔÕ]/g, 'o').replace(/[éèëêÉÈËÊ]/g, 'e').replace(/[úùüûÚÙÜÛ]/g, 'u').replace(/[íìïîÍÌÏÎ]/g, 'i').replace(/[^a-zA-Z0-9]/g, '').toLowerCase()}.streamify.io` })}`;
      }

      if (fqdn === '') {
        // eslint-disable-next-line
        return `${this.$t('login.fqdn_addr_subdomain', { subdomain: `${name.replace(/[áàäâãåÁÀÄÂÃÅ]/g, 'a').replace(/[óòöôõÓÒÖÔÕ]/g, 'o').replace(/[éèëêÉÈËÊ]/g, 'e').replace(/[úùüûÚÙÜÛ]/g, 'u').replace(/[íìïîÍÌÏÎ]/g, 'i').replace(/[^a-zA-Z0-9]/g, '').toLowerCase()}.streamify.io` })}`;
      }

      // eslint-disable-next-line
      return `${this.$t('login.fqdn_addr_subdomain', { subdomain: `${fqdn.replace(/[áàäâãåÁÀÄÂÃÅ]/g, 'a').replace(/[óòöôõÓÒÖÔÕ]/g, 'o').replace(/[éèëêÉÈËÊ]/g, 'e').replace(/[úùüûÚÙÜÛ]/g, 'u').replace(/[íìïîÍÌÏÎ]/g, 'i').replace(/[^a-zA-Z0-9]/g, '').toLowerCase()}.streamify.io` })}`;
    },
  },
  methods: {
    paginate(page) {
      this.current_page = page;
      this.getConnectedAccounts();
    },
    /**
     * Return a formated date
     * @param {String} date
     * @returns {String}
     */
    created(date) {
      return moment(date).format('YYYY-MM-DD HH:mm');
    },
    /**
     * Get connected accounts
     */
    async getConnectedAccounts() {
      this.loading = true;

      const payload = {
        page: this.current_page,
        per_page: this.per_page,
      };

      if (this.searchString !== '') {
        payload.search = this.searchString;
      }

      const {
        accounts,
        total,
        pagination: {
          current_page = 1,
          max_page = 1,
        } = {},
      } = await this.$store.dispatch('AccountManager/getChildAccounts', payload);

      this.accounts = accounts;
      this.current_page = current_page;
      this.max_page = max_page;
      this.total_accounts = parseInt(total, 10) === 0 || parseInt(total, 10) === 1 ? total : total - 1;
      this.loading = false;
    },
    /**
     * Open modal for creation of a new connected account
     */
    addNewConnectedAccount() {
      this.addConnectedAccount = true;
      this.selectedAccount = null;
    },
    /**
     * Assume account
     * @param {Number} id
     */
    async assumeAccount(id) {
      await this.$store.dispatch('AccountManager/assumeAccount', { account: id });
    },
    /**
     * Unassume account
     */
    async unassumeAccount() {
      this.$store.dispatch('AccountManager/unassumeAccount');
    },
    /**
     * Select account
     * @param {Number} id
     */
    selectAccount(id) {
      const { selectedAccount } = this;
      this.selectedAccount = selectedAccount === id ? null : id;
      this.addConnectedAccount = false;
    },
    cancelCreateAccount() {
      this.addConnectedAccount = false;
    },
    async createAccount() {
      const { newAccount: { fqdn, name } } = this;
      this.createState = 'creating';

      const payload = {
        name,
      };

      if (fqdn !== '') {
        payload.fqdn = `${fqdn}.streamify.io`;
      }

      const { error } = await this.$store.dispatch('AccountManager/createChildAccount', payload);

      if (error) {
        swal({ title: this.$t('errors.error_occured'), text: error.message, icon: 'error' });
        this.createState = '';
        return;
      }

      this.createState = 'created';

      await this.getConnectedAccounts();
      await this.$store.dispatch('AccountManager/getAccountSettings');

      setTimeout(() => {
        this.createState = '';
        this.newAccount = {
          name: '',
          fqdn: '',
        };

        if (this.accountSettings.subscription.sub_accounts.additional === 0) {
          this.addConnectedAccount = false;
        }
      }, 2000);
    },
  },
  watch: {
    show(val) {
      if (val && this.accounts.length === 0) {
        this.getConnectedAccounts();
      }
    },
    searchString(val) {
      clearTimeout(this.searchTimeout);
      this.searchString = val;
      this.searchTimeout = setTimeout(() => {
        this.getConnectedAccounts();
      }, 250);
    },
  },
  data() {
    return {
      pagination: {
        page: 1,
        perPage: 10,
        maxPage: 1,
      },
      current_page: 1,
      max_page: 1,
      per_page: 10,
      total_accounts: 0,
      accounts: [],
      loading: true,
      searchString: '',
      addConnectedAccount: false,
      selectedAccount: null,
      createState: '',
      searchTimeout: null,
      newAccount: {
        name: '',
        fqdn: '',
      },
    };
  },
  beforeDestroy() {
    clearTimeout(this.searchTimeout);
  },
};
</script>

<style lang="scss">

.connected-accounts-settings {
  .searchArea {
    background-color: white;
    border-radius: 10px 10px 0 0;
    border-bottom: 1px solid #E7E9EC;
  }

  .no-connected-accounts {
    text-align: center;
  }

  p.info {
    font-size: .8rem;
    word-break: break-word;
    &.fqdn {
      .blurry {
        opacity: .4;
      }
    }
  }

  .modal-inner {
    @media (min-width: 650px) {
      min-width: 600px;
      max-width: 600px;
    }
  }
}
</style>
